/**
 * Make a frame paint when it is shown, the way a resize does.
 *
 * Since every space's panes stay mounted (`display: none` unless active), an
 * app frame can load, or be navigated, while it has no box. Shown again it
 * stays white: the document is there, the bridge answers, nothing is painted,
 * and the first thing that fixes it is anything that resizes the frame, such
 * as opening a second pane. So this does that resize itself, one pixel
 * narrower for two frames, whenever the frame goes from no box to a box and
 * whenever it loads. `max-width` rather than `width`, because it holds whether
 * the pane is a flex row or a column.
 *
 * A NUDGE AND NOT A REMOUNT. Remounting paints too, and it reloads the app,
 * which throws away whatever the person had open in it.
 *
 * A ref callback (React 19 runs the returned teardown), so a frame that is
 * remounted by its key gets watched again rather than keeping the old one's.
 */
export function repaintWhenShown(el: HTMLIFrameElement | null) {
  if (!el) return
  let raf = 0
  const nudge = () => {
    cancelAnimationFrame(raf)
    el.style.maxWidth = 'calc(100% - 1px)'
    raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => {
        el.style.maxWidth = ''
      })
    })
  }
  let shown = el.getClientRects().length > 0
  const observer = new ResizeObserver(() => {
    const now = el.getClientRects().length > 0
    if (now && !shown) nudge()
    shown = now
  })
  observer.observe(el)
  el.addEventListener('load', nudge)
  return () => {
    cancelAnimationFrame(raf)
    observer.disconnect()
    el.removeEventListener('load', nudge)
    el.style.maxWidth = ''
  }
}
